import type { TokensOutput } from 'used-vehicle-contracts';
import { resolveApiBaseUrl } from '../env-config';
import { authTokenStore } from './auth-token';

export const LOGIN_PATH = '/login';
export const REFRESH_PATH = '/auth/refresh';

/**
 * The silent refresh, shared by **every** authenticated request the app makes (plan §2.5).
 *
 * It lived inside `orpc-client.ts` until documents arrived, and then it had to move: a multipart upload
 * goes through `XMLHttpRequest` (for progress events) and a document download goes through `fetch` (for
 * an object URL), and neither passes through the oRPC link. Leaving the refresh where it was would have
 * meant a photo upload that 401s in the fifteenth minute of a session simply failing, while every other
 * call in the same tab quietly renewed itself.
 *
 * The two properties that matter are both about *not spending the refresh token twice*:
 *
 *  - **One refresh at a time.** Several calls usually expire together; without the shared promise each
 *    would spend the refresh token, and the second exchange would be refused — ending a healthy session.
 *  - **A refused refresh, not a 401, ends the session.** An expired access token is an ordinary event.
 *    Only a refresh the service itself rejects — a disabled user, a disabled membership, a bumped token
 *    version — means the person really has to sign in again.
 */
export const endSession = (): void => {
  authTokenStore.clear();

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.replace(LOGIN_PATH);
  }
};

export const apiUrl = (path: string): string => `${resolveApiBaseUrl().replace(/\/$/, '')}${path}`;

let inFlightRefresh: Promise<TokensOutput | null> | null = null;

const refreshSession = async (): Promise<TokensOutput | null> => {
  const refreshToken = authTokenStore.getRefreshToken();

  if (!refreshToken) {
    return null;
  }

  const response = await fetch(apiUrl(REFRESH_PATH), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    return null;
  }

  const session = (await response.json()) as TokensOutput;

  authTokenStore.setSession(session);

  return session;
};

/** Renews the session at most once per burst; resolves to `null` when the service refuses. */
export const refreshOnce = (): Promise<TokensOutput | null> => {
  inFlightRefresh ??= refreshSession()
    .catch(() => null)
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
};

/**
 * Runs a request that carries the bearer token, and replays it once if the token had just expired.
 *
 * `send` receives the token to use, so the caller decides *how* the request is made — `fetch` for a
 * download, `XMLHttpRequest` for an upload with progress — while the renewal rule stays in one place.
 */
export const withFreshSession = async <T extends { status: number }>(send: (accessToken: string | null) => Promise<T>): Promise<T> => {
  const response = await send(authTokenStore.getAccessToken());

  if (response.status !== 401) {
    return response;
  }

  const session = await refreshOnce();

  if (!session) {
    endSession();

    return response;
  }

  const retried = await send(session.accessToken);

  if (retried.status === 401) {
    endSession();
  }

  return retried;
};
